import type {
  DesignCollectionId,
  DesignDeleteCollectionGroupInput,
  DesignDeleteInvalidSystemInput,
  DesignDeleteSystemInput,
  DesignSystemFailure,
  DesignSystemReadModel,
  DesignSystemRevision,
  DesignSystemSlug,
} from "@afar-research/contracts";

import {
  DESIGN_DELETE_AND_RECREATE_GUIDANCE,
  DESIGN_WORKSPACE_RELATIVE_PATH,
  parseDesignSystemDocument,
} from "./designWorkspace.ts";

export const DESIGN_SYSTEMS_DIRECTORY = `${DESIGN_WORKSPACE_RELATIVE_PATH}/design-system`;

export type DesignSystemMutationFailure = {
  readonly ok: false;
  readonly reason: DesignSystemFailure;
  readonly message: string;
  readonly relativePath?: string;
  readonly designSystemSlug?: string;
  readonly expectedRevision?: DesignSystemRevision;
  readonly observedRevision?: DesignSystemRevision;
  readonly recovery?: string;
};

export type DesignSystemDeletePlan =
  | {
      readonly ok: true;
      readonly action: "already-applied";
      readonly designSystemSlug: string;
      readonly slug: DesignSystemSlug;
      readonly name: string;
      readonly relativePath: string;
    }
  | {
      readonly ok: true;
      readonly action: "delete";
      readonly designSystemSlug: string;
      readonly slug: DesignSystemSlug;
      readonly name: string;
      readonly relativePath: string;
    }
  | DesignSystemMutationFailure;

export function designSystemRelativePath(slug: DesignSystemSlug): string {
  return `${DESIGN_SYSTEMS_DIRECTORY}/${slug}`;
}

export function designSystemAuthorityRelativePath(slug: DesignSystemSlug): string {
  return `${designSystemRelativePath(slug)}/DESIGN.md`;
}

/** On-disk directory name for each Design collection tile. Samples → outputs/. */
export function designCollectionDirectoryName(collection: DesignCollectionId): string {
  switch (collection) {
    case "templates":
      return "templates";
    case "examples":
      return "examples";
    case "samples":
      return "outputs";
  }
}

export function designCollectionGroupRelativePath(input: {
  readonly designSystemSlug: DesignSystemSlug;
  readonly collection: DesignCollectionId;
  readonly groupSlug: string;
}): string {
  return `${designSystemRelativePath(input.designSystemSlug)}/${designCollectionDirectoryName(input.collection)}/${input.groupSlug}`;
}

export function deriveDesignSystemSlug(displayName: string): DesignSystemSlug {
  const slug = displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
  return (slug.length > 0 ? slug : "design-system") as DesignSystemSlug;
}

/**
 * Set a Design System authority's `name` and `slug` and leave every other line
 * of its frontmatter alone. The Design Workspace healer is the only caller: it
 * runs when a directory and its `slug:` disagree, so its one job is the slug.
 *
 * It used to drop `id:` as well, from when `id` was the Design System identity
 * that `slug` replaced. That strip is gone: the Rename Skill Bundle preserves
 * `id`, and a repair that silently deleted a field a rename keeps would be the
 * only path that loses it.
 */
export function rewriteDesignSystemDocument(input: {
  readonly content: string;
  readonly displayName: string;
  readonly storageSlug: DesignSystemSlug;
}): { readonly ok: true; readonly content: string; readonly slug: string } | null {
  const parsed = parseDesignSystemDocument(input.content);
  if (parsed === null) return null;
  const lines = input.content.replace(/^\uFEFF/, "").split(/\r?\n/);
  if (lines[0] !== "---") return null;
  const end = lines.indexOf("---", 1);
  if (end < 0) return null;
  // JSON strings are valid double-quoted YAML scalars and keep values such as `null`, `#`,
  // colons, quotes, and newlines from changing frontmatter semantics.
  const serializedDisplayName = JSON.stringify(input.displayName);
  const frontmatter = lines.slice(1, end).map((line) => {
    if (/^name:\s*/i.test(line)) return `name: ${serializedDisplayName}`;
    if (/^slug:\s*/i.test(line)) return `slug: ${input.storageSlug}`;
    return line;
  });
  const hasName = frontmatter.some((line) => /^name:\s*/i.test(line));
  const hasSlug = frontmatter.some((line) => /^slug:\s*/i.test(line));
  const nextFrontmatter = [
    ...frontmatter,
    ...(hasName ? [] : [`name: ${serializedDisplayName}`]),
    ...(hasSlug ? [] : [`slug: ${input.storageSlug}`]),
  ];
  return {
    ok: true,
    slug: input.storageSlug,
    content: ["---", ...nextFrontmatter, "---", ...lines.slice(end + 1)].join("\n"),
  };
}

export function classifyDesignSystems(input: {
  readonly systems: readonly DesignSystemReadModel[];
  readonly invalidSystems: readonly { relativePath: string; detail: string }[];
}): {
  readonly systems: readonly DesignSystemReadModel[];
  readonly invalidSystems: readonly { relativePath: string; detail: string }[];
} {
  const bySlug = new Map<string, string[]>();
  for (const system of input.systems) {
    bySlug.set(system.slug, [...(bySlug.get(system.slug) ?? []), system.relativePath]);
  }
  const duplicatePaths = new Set([...bySlug.values()].filter((paths) => paths.length > 1).flat());
  const duplicates = input.systems
    .filter((system) => duplicatePaths.has(system.relativePath))
    .map((system) => ({
      relativePath: system.relativePath,
      detail: `Design System storage slug collides for '${system.name}'.`,
    }));
  return {
    systems: input.systems
      .filter((system) => !duplicatePaths.has(system.relativePath))
      .toSorted((left, right) => left.slug.localeCompare(right.slug)),
    invalidSystems: [...input.invalidSystems, ...duplicates].toSorted((left, right) =>
      left.relativePath.localeCompare(right.relativePath),
    ),
  };
}

export function decideDesignSystemDelete(input: {
  readonly request: DesignDeleteSystemInput;
  readonly systems: readonly DesignSystemReadModel[];
  readonly systemStillPresent: boolean;
  readonly allowAlreadyApplied?: boolean;
}): DesignSystemDeletePlan {
  const current = input.systems.find((system) => system.slug === input.request.designSystemSlug);
  if (!current) {
    if (!input.systemStillPresent && input.allowAlreadyApplied === true) {
      return {
        ok: true,
        action: "already-applied",
        designSystemSlug: input.request.designSystemSlug,
        slug: input.request.confirmationSlug,
        name: input.request.confirmationName,
        relativePath: designSystemRelativePath(input.request.confirmationSlug),
      };
    }
    return {
      ok: false,
      reason: "system-not-found",
      message: `Design System '${input.request.designSystemSlug}' was not found.`,
      designSystemSlug: input.request.designSystemSlug,
      recovery: DESIGN_DELETE_AND_RECREATE_GUIDANCE,
    };
  }
  if (
    current.slug !== input.request.confirmationSlug ||
    current.name !== input.request.confirmationName
  ) {
    return {
      ok: false,
      reason: "confirmation-mismatch",
      message: `Delete confirmation does not match Design System '${current.name}' (${current.slug}).`,
      relativePath: current.relativePath,
      designSystemSlug: current.slug,
      recovery: `Confirm deletion using the exact name '${current.name}' and storage slug '${current.slug}'.`,
    };
  }
  if (current.revision !== input.request.expectedRevision) {
    return {
      ok: false,
      reason: "revision-conflict",
      message: "The Design System changed before delete could begin.",
      relativePath: current.relativePath,
      designSystemSlug: current.slug,
      expectedRevision: input.request.expectedRevision,
      observedRevision: current.revision,
      recovery: DESIGN_DELETE_AND_RECREATE_GUIDANCE,
    };
  }
  return {
    ok: true,
    action: "delete",
    designSystemSlug: current.slug,
    slug: current.slug,
    name: current.name,
    relativePath: designSystemRelativePath(current.slug),
  };
}

export function describeDesignSystemDeleteEffect(input: {
  readonly name: string;
  readonly slug: DesignSystemSlug;
}): string {
  return [
    `Delete Design System "${input.name}"`,
    `storage .afar-research/design/design-system/${input.slug}/`,
    "including DESIGN.md and any optional orientation or derived files under that directory.",
    "This does not start an Agent Provider conversation and leaves source-control unstaged.",
  ].join(" — ");
}

/**
 * The storage directory a listed invalid Design System addresses.
 *
 * `DesignInvalidSystem.relativePath` is not one shape: a badly named directory reports the
 * directory, while a missing or unparseable authority reports that system's `DESIGN.md`.
 * Delete removes the Design System directory either way, so the reported path is narrowed to
 * exactly one segment under the Design Systems directory before anything touches the disk.
 */
export function designInvalidSystemStorageRelativePath(relativePath: string): string | null {
  const normalized = relativePath.replaceAll("\\", "/").replace(/\/+$/, "");
  const prefix = `${DESIGN_SYSTEMS_DIRECTORY}/`;
  if (!normalized.startsWith(prefix)) return null;
  const segments = normalized.slice(prefix.length).split("/");
  const slug = segments[0];
  if (slug === undefined || slug.length === 0 || slug === "." || slug === "..") return null;
  // A listing only ever names the directory itself or the authority directly inside it.
  if (segments.length > 2 || (segments.length === 2 && segments[1] !== "DESIGN.md")) return null;
  return `${DESIGN_SYSTEMS_DIRECTORY}/${slug}`;
}

export type DesignInvalidSystemDeletePlan =
  | {
      readonly ok: true;
      readonly action: "already-applied";
      readonly relativePath: string;
      readonly storageRelativePath: string;
    }
  | {
      readonly ok: true;
      readonly action: "delete";
      readonly relativePath: string;
      readonly storageRelativePath: string;
    }
  | DesignSystemMutationFailure;

/**
 * Delete for a Design System that has no slug and no revision to check. The guard the healthy
 * path gets from `expectedRevision` is replaced by two weaker ones that are all a corrupt system
 * can offer: the echoed path, and the requirement that the listing still calls it invalid.
 */
export function decideDesignInvalidSystemDelete(input: {
  readonly request: DesignDeleteInvalidSystemInput;
  readonly systems: readonly DesignSystemReadModel[];
  readonly invalidSystems: readonly { relativePath: string; detail: string }[];
  readonly targetStillPresent: boolean;
  readonly allowAlreadyApplied?: boolean;
}): DesignInvalidSystemDeletePlan {
  const { relativePath, confirmationRelativePath } = input.request;
  if (relativePath !== confirmationRelativePath) {
    return {
      ok: false,
      reason: "confirmation-mismatch",
      message: `Delete confirmation does not match invalid Design System '${relativePath}'.`,
      relativePath,
      recovery: `Confirm deletion using the exact path '${relativePath}'.`,
    };
  }

  const storageRelativePath = designInvalidSystemStorageRelativePath(relativePath);
  if (storageRelativePath === null) {
    return {
      ok: false,
      reason: "unsafe-path",
      message: `'${relativePath}' does not address a Design System under ${DESIGN_SYSTEMS_DIRECTORY}/.`,
      relativePath,
      recovery: "Delete only a path that design.listSystems reported as an invalid Design System.",
    };
  }

  const listed = input.invalidSystems.some(
    (invalid) =>
      designInvalidSystemStorageRelativePath(invalid.relativePath) === storageRelativePath,
  );
  if (!listed) {
    const healthy = input.systems.find(
      (system) =>
        designInvalidSystemStorageRelativePath(system.relativePath) === storageRelativePath,
    );
    if (healthy) {
      return {
        ok: false,
        reason: "system-not-invalid",
        message: `Design System '${healthy.name}' (${healthy.slug}) is healthy and cannot be deleted by path.`,
        relativePath,
        designSystemSlug: healthy.slug,
        recovery:
          "Delete a healthy Design System through design.deleteSystem, which checks its revision.",
      };
    }
    if (!input.targetStillPresent && input.allowAlreadyApplied === true) {
      return { ok: true, action: "already-applied", relativePath, storageRelativePath };
    }
    return {
      ok: false,
      reason: "system-not-found",
      message: `No invalid Design System was found at '${relativePath}'.`,
      relativePath,
      recovery: DESIGN_DELETE_AND_RECREATE_GUIDANCE,
    };
  }

  if (!input.targetStillPresent) {
    if (input.allowAlreadyApplied === true) {
      return { ok: true, action: "already-applied", relativePath, storageRelativePath };
    }
    return {
      ok: false,
      reason: "system-not-found",
      message: `Invalid Design System storage '${storageRelativePath}' disappeared before delete could begin.`,
      relativePath,
      recovery: DESIGN_DELETE_AND_RECREATE_GUIDANCE,
    };
  }

  return { ok: true, action: "delete", relativePath, storageRelativePath };
}

export function describeDesignInvalidSystemDeleteEffect(storageRelativePath: string): string {
  return [
    `Delete the invalid Design System stored at ${storageRelativePath}/`,
    "including every file under that directory",
    "This does not start an Agent Provider conversation and leaves source-control unstaged.",
  ].join(" — ");
}

export type DesignCollectionGroupDeletePlan =
  | {
      readonly ok: true;
      readonly action: "already-applied";
      readonly designSystemSlug: DesignSystemSlug;
      readonly collection: DesignCollectionId;
      readonly groupSlug: string;
      readonly relativePath: string;
    }
  | {
      readonly ok: true;
      readonly action: "delete";
      readonly designSystemSlug: DesignSystemSlug;
      readonly collection: DesignCollectionId;
      readonly groupSlug: string;
      readonly relativePath: string;
    }
  | DesignSystemMutationFailure;

export function decideDesignCollectionGroupDelete(input: {
  readonly request: DesignDeleteCollectionGroupInput;
  readonly systems: readonly DesignSystemReadModel[];
  readonly groupStillPresent: boolean;
  readonly allowAlreadyApplied?: boolean;
}): DesignCollectionGroupDeletePlan {
  const current = input.systems.find((system) => system.slug === input.request.designSystemSlug);
  const relativePath = designCollectionGroupRelativePath({
    designSystemSlug: input.request.designSystemSlug,
    collection: input.request.collection,
    groupSlug: input.request.confirmationGroupSlug,
  });

  if (input.request.groupSlug !== input.request.confirmationGroupSlug) {
    return {
      ok: false,
      reason: "confirmation-mismatch",
      message: `Delete confirmation does not match group '${input.request.groupSlug}'.`,
      relativePath,
      designSystemSlug: input.request.designSystemSlug,
      recovery: `Confirm deletion using the exact group slug '${input.request.groupSlug}'.`,
    };
  }

  if (!current) {
    return {
      ok: false,
      reason: "system-not-found",
      message: `Design System '${input.request.designSystemSlug}' was not found.`,
      designSystemSlug: input.request.designSystemSlug,
      recovery: DESIGN_DELETE_AND_RECREATE_GUIDANCE,
    };
  }

  if (current.revision !== input.request.expectedRevision) {
    return {
      ok: false,
      reason: "revision-conflict",
      message: "The Design System changed before group delete could begin.",
      relativePath: current.relativePath,
      designSystemSlug: current.slug,
      expectedRevision: input.request.expectedRevision,
      observedRevision: current.revision,
      recovery: DESIGN_DELETE_AND_RECREATE_GUIDANCE,
    };
  }

  if (!input.groupStillPresent) {
    if (input.allowAlreadyApplied === true) {
      return {
        ok: true,
        action: "already-applied",
        designSystemSlug: current.slug,
        collection: input.request.collection,
        groupSlug: input.request.groupSlug,
        relativePath,
      };
    }
    return {
      ok: false,
      reason: "group-not-found",
      message: `Design ${input.request.collection} group '${input.request.groupSlug}' was not found.`,
      relativePath,
      designSystemSlug: current.slug,
      recovery: `Refresh the Design inventory, then retry delete for an existing group under ${designCollectionDirectoryName(input.request.collection)}/.`,
    };
  }

  return {
    ok: true,
    action: "delete",
    designSystemSlug: current.slug,
    collection: input.request.collection,
    groupSlug: input.request.groupSlug,
    relativePath,
  };
}

export function describeDesignCollectionGroupDeleteEffect(input: {
  readonly designSystemName: string;
  readonly designSystemSlug: DesignSystemSlug;
  readonly collection: DesignCollectionId;
  readonly groupSlug: string;
}): string {
  const dir = designCollectionDirectoryName(input.collection);
  return [
    `Delete ${input.collection} group "${input.groupSlug}"`,
    `from Design System "${input.designSystemName}"`,
    `storage .afar-research/design/design-system/${input.designSystemSlug}/${dir}/${input.groupSlug}/`,
    "This does not start an Agent Provider conversation and leaves source-control unstaged.",
  ].join(" — ");
}
